/**
 * Shapes the marine catalog response into grid rows and class rows.
 *
 * A boat's CLASS stands where a car's TRIM stands: it is the child row under a
 * Make + Model group. The server returns one row per model GENERATION
 * (`year_start`–`year_end`), each carrying exactly one `boat_class`, so the
 * mapping is not one-to-one and the collapse rule below is the whole point of
 * this module.
 *
 * Dev data makes this look more elaborate than it is: 906 groups over 918 rows,
 * averaging 1.01 generations, maximum 2. The rule exists to stop the rare
 * divergent pair from being silently merged and losing a set of dimensions —
 * not because multi-generation models are common.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/marineCatalogGrouping.test.mjs`. Do not add imports.
 */

/** Label for a class that the feed left blank. Sorts before every real class. */
export const UNCLASSIFIED = 'Unclassified';

/** En dash, not a hyphen — year ranges are ranges, not compounds. */
const EN_DASH = '–';

/**
 * "2018–2024" · "2018" · "2018+" (open-ended) · "—" (unknown).
 *
 * `year_end` NULL means open-ended, per the column comment — NOT unknown. The
 * two render differently on purpose.
 *
 * A null `year_start` with a bounded end renders as the end year alone. That
 * shape is not expected: `year_start` is part of the natural key
 * (UNIQUE make_id, model_name, year_start), so the feed cannot produce it.
 */
export function formatYearSpan(yearStart, yearEnd) {
  const start = Number(yearStart);
  const end = Number(yearEnd);
  const hasStart = Number.isFinite(start) && start > 0;
  const hasEnd = Number.isFinite(end) && end > 0;
  if (!hasStart && !hasEnd) return '—';
  if (!hasStart) return `${end}`;
  if (!hasEnd) return `${start}+`;
  if (start === end) return `${start}`;
  return `${start}${EN_DASH}${end}`;
}

/**
 * The dimension fingerprint two generations must share to collapse into one
 * class row.
 *
 * Stringified before comparison so the key is stable regardless of how the
 * numbers arrive. Verified against dev 2026-08-21: the catalog endpoint sends
 * `loa_ft` / `beam_ft` as JS NUMBERS (24.58, 8.5) — the pool's typeCast
 * converts NEWDECIMAL — not as the DECIMAL strings an earlier draft of this
 * module assumed.
 *
 * That difference is invisible here, because every generation within one
 * response is uniformly typed: all numbers collapse against all numbers. The
 * case this key could NOT handle is a response mixing a raw 26.00 with a
 * '26.00' for the same true dimension, since String(26.00) is "26". No such
 * path exists, so hardening against it is deliberately left undone rather than
 * loosening the key to a numeric compare.
 *
 * `size_tier` is part of the fingerprint too. It is not a dimension, but it is
 * the value the tier editor writes per row: two generations that differ only
 * by tier must stay two rows, or the modal shows one value for both and a
 * save silently overwrites one of them.
 */
function dimensionKey(g) {
  const norm = (v) => (v === null || v === undefined ? '' : String(v).trim());
  return [
    norm(g.loa_ft), norm(g.beam_ft), norm(g.hull_height_in), norm(g.hull_code), norm(g.size_tier),
  ].join('|');
}

function classOf(g) {
  const raw = g && g.boat_class;
  const v = raw === null || raw === undefined ? '' : String(raw).trim();
  return v === '' ? null : v;
}

/**
 * A year, or null.
 *
 * `Number(null)` is 0 and 0 is finite, so a bare `Number.isFinite` test reads a
 * NULL `year_end` — the column's documented "open-ended" marker — as the year
 * zero. That silently defeated the subsumption rule below: an open-ended
 * generation failed to widen a bounded one, and a model still in production
 * rendered as a closed range.
 */
function toYear(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const generationsOf = (group) =>
  (group && Array.isArray(group.generations)) ? group.generations.filter(Boolean) : [];

/**
 * Class rows for one Make + Model group.
 *
 * Grouped by (class, dimensions): same class AND same dimensions is one row
 * whose span is the union; same class with DIFFERENT dimensions stays two rows
 * and both are suffixed with their year span, so the grid never shows two
 * identically-labeled rows.
 */
export function classRowsFor(group) {
  const generations = generationsOf(group);
  if (generations.length === 0) return [];

  const buckets = new Map();
  generations.forEach((g) => {
    const boatClass = classOf(g);
    const key = `${boatClass ?? ''}::${dimensionKey(g)}`;
    const existing = buckets.get(key);
    const start = toYear(g.year_start);
    const end = toYear(g.year_end);
    if (existing) {
      existing.generationIds.push(g.id);
      if (g.external_id) existing.externalIds.push(g.external_id);
      if (start !== null && (existing.yearStart === null || start < existing.yearStart)) {
        existing.yearStart = start;
      }
      // An open-ended generation (null year_end) subsumes any bounded one.
      if (end === null) existing.yearEnd = null;
      else if (existing.yearEnd !== null && end > existing.yearEnd) existing.yearEnd = end;
      return;
    }
    buckets.set(key, {
      key,
      boatClass,
      yearStart: start,
      yearEnd: end,
      generationIds: [g.id],
      externalIds: g.external_id ? [g.external_id] : [],
      loaFt: g.loa_ft ?? null,
      beamFt: g.beam_ft ?? null,
      hullHeightIn: g.hull_height_in ?? null,
      hullCode: g.hull_code ?? null,
      sizeTier: g.size_tier ?? null,
    });
  });

  const rows = [...buckets.values()];

  // Unclassified first, then class name, then earliest year.
  rows.sort((a, b) => {
    if ((a.boatClass === null) !== (b.boatClass === null)) return a.boatClass === null ? -1 : 1;
    const byClass = String(a.boatClass ?? '').localeCompare(String(b.boatClass ?? ''));
    if (byClass !== 0) return byClass;
    return (a.yearStart ?? 0) - (b.yearStart ?? 0);
  });

  // Suffix the year span ONLY where a class would otherwise appear twice.
  const classCounts = rows.reduce((acc, r) => {
    const k = r.boatClass ?? UNCLASSIFIED;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return rows.map((r) => {
    const base = r.boatClass ?? UNCLASSIFIED;
    const needsSuffix = classCounts[base] > 1;
    return {
      ...r,
      label: needsSuffix ? `${base} (${formatYearSpan(r.yearStart, r.yearEnd)})` : base,
    };
  });
}

/**
 * Freeboard completeness over an already-computed set of class rows.
 *
 * Counting generations would overstate the work: two generations that collapse
 * into one row are one hull to measure, not two.
 */
function freeboardFrom(rows) {
  const missing = rows.filter((r) => {
    const n = Number(r.hullHeightIn);
    return !(Number.isFinite(n) && n > 0);
  }).length;
  return { total: rows.length, missing, complete: rows.length > 0 && missing === 0 };
}

/** Freeboard completeness for a group, counted over CLASS ROWS. */
export function freeboardStatusFor(group) {
  return freeboardFrom(classRowsFor(group));
}

/**
 * Make-filter options, derived from the RESPONSE.
 *
 * Deliberately not from `GET /api/boats/makes`: `boat_make` holds 15,998 rows —
 * the USCG manufacturer registry — and only 53 of them carry any model. A
 * dropdown of the full list would be unusable and would imply models exist
 * where none do.
 */
export function makeOptionsFrom(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const names = [...new Set(list.map((g) => g && g.make_name).filter(Boolean))];
  names.sort((a, b) => String(a).localeCompare(String(b)));
  return ['All Makes', ...names];
}

/** One grid row per Make + Model group. */
export function toGridRows(groups) {
  const list = Array.isArray(groups) ? groups : [];
  return list.filter(Boolean).map((group) => {
    const rows = classRowsFor(group);
    const starts = rows.map((r) => r.yearStart).filter((y) => y !== null);
    const ends = rows.map((r) => r.yearEnd);
    const openEnded = ends.some((y) => y === null);
    const bounded = ends.filter((y) => y !== null);
    return {
      id: `${group.make_name}::${group.model_name}`,
      makeName: group.make_name,
      modelName: group.model_name,
      yearRange: formatYearSpan(
        starts.length ? Math.min(...starts) : null,
        openEnded || bounded.length === 0 ? null : Math.max(...bounded),
      ),
      classCount: rows.length,
      classRows: rows,
      freeboard: freeboardFrom(rows),
    };
  });
}
